import { TitleCasePipe } from '@angular/common';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { AuditableFilterService } from '../../Filter/services/auditable-filter.service';
import { AuditableModel } from '../../Misc/models/auditable.model';
import { UsersService } from '../../../Core/Users/services/users.service';
import { ListEntitiesVisibilityEnum } from '../../MonitorShared/enum/list-entities-visibility.enum';
import { FilterTypeCheckboxModel } from '../../Filter/models/filter-type-checkbox.model';
import { ActionsEnum } from '../../Basic/enums/actions.enum';
import { LeftSidebarService } from './left-sidebar.service';
import { LeftSidebarListItemButton } from '../models/left-sidebar-list-item-button.model';
import { LeftSidebarListItem } from '../models/left-sidebar-list-item.model';
import { DateQueryModel } from '../../Forms/models/date-query.model';
import { TableColumnModel } from '../../Forms/models/table-column.model';
import { ChipModel } from '../../Forms/models/chip.model';
import { TableColumnInputEnum } from '../../Forms/enums/table-column-input-type.enum';
import { FilterTypeModel } from '../../Filter/models/abstract-filter-type.model';
import { DisplayGroupModel } from '../models/display-group.model';
import { DisplayGroupsService } from './display-groups.service';
import { DisplayGroupTypeEnum } from '../../../api/displayGroups/enums/display-group-type.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { Mode } from '../../Layout/enums/mode.enum';


export interface LhpManagementConfig {
    itemSource: Observable<any[]>;
    columns?: TableColumnModel[];
    customFilters?: FilterTypeModel[];
    setUpCustomFilters?: () => void;
    applyCustomFilters?: (items: GroupedAuditableModel[]) => GroupedAuditableModel[];
    resetCustomFilters?: () => void;
}

export interface ItemRow {
    name: string;
    createdDate?: string;
    createdDateRaw?: number;
    createdByUserId?: number;
    createdByUser?: string;
    lastModifiedDate?: string;
    lastModifiedDateRaw?: number;
    lastModifiedByUserId?: number;
    lastModifiedByUser?: string;
    selected?: boolean;
    status?: string;
    type?: string;
    deliveryMethod?: string;
    count?: number;
    tags?: ChipModel[];
    tagIds?: number[];
    autoTags?: ChipModel[];
    autoTagIds?: number[];
    original: any;
    actions: MenuItem[];
    visibility: string;
    editDisabled: boolean;
    loading: boolean;
}

export interface GroupedAuditableModel extends AuditableModel {
    groupId?: number;
    isFavorite?: boolean;
}

export type StatusLhpItem = GroupedAuditableModel & {status: string};
export type TypeLhpItem = GroupedAuditableModel & {type: string};
export type AlertTypeLhpItem = GroupedAuditableModel & {alertType: string};
export type BriefingDeliveryMethodLhpItem = GroupedAuditableModel & {deliveryMethod: string};

export abstract class LeftSidebarManagementService extends AuditableFilterService {

    protected subscriptions: Subscription[] = [];
    protected allListItems: GroupedAuditableModel[] = [];
    protected currentMode: Mode;
    protected currentConfig: LhpManagementConfig;
    protected listItems: GroupedAuditableModel[] = [];
    protected listItemsProvider$: BehaviorSubject<GroupedAuditableModel[]> = new BehaviorSubject<GroupedAuditableModel[]>(this.listItems);
    protected groupedListItems: DisplayGroupModel[] = [];
    protected groupedListItemsProvider$: BehaviorSubject<DisplayGroupModel[]> = new BehaviorSubject<DisplayGroupModel[]>(this.groupedListItems);
    protected columnsProvider$: BehaviorSubject<TableColumnModel[]> = new BehaviorSubject<TableColumnModel[]>([]);
    protected loading$: BehaviorSubject<boolean> = new BehaviorSubject(true);
    protected lhpFilters: FilterTypeModel[];
    protected resetSelectionProvider$: BehaviorSubject<boolean> = new BehaviorSubject(false);

    public itemNameColumn: TableColumnModel = {
        type: TableColumnInputEnum.text,
        heading: 'Common.lhp.manageList.dialog.column.name',
        field: 'name',
        tooltipField: 'name',
        fieldMaxLength: 64,
        sortable: true,
        sortType: 'text',
        cellStyleClass: 'agpr-manageLhpList-tableName',
        styleClass: 'agpr-manageLhpList-tableName',
        selectable: true,
        selectField: 'selected',
        width: 350
    };

    protected metadataColumns: TableColumnModel[] = [
        {
            heading: 'Common.lhp.manageList.dialog.column.visibility',
            field: 'visibility',
            sortable: true,
            sortType: 'text',
            width: 80
        },
        {
            heading: 'Discover.targeting.panels.createdUser',
            field: 'createdByUser',
            sortable: true,
            sortType: 'text',
            width: 150
        },
        {
            heading: 'Discover.targeting.panels.createdDate',
            field: 'createdDate',
            sortable: true,
            sortField: 'createdDateRaw',
            width: 150,
        },
        {
            heading: 'Discover.targeting.panels.modifiedUser',
            field: 'lastModifiedByUser',
            sortable: true,
            sortType: 'text',
            width: 150
        },
        {
            heading: 'Discover.targeting.panels.modifiedDate',
            field: 'lastModifiedDate',
            sortable: true,
            sortField: 'lastModifiedDateRaw',
            width: 150,
        }
    ];

    protected statusCustomColumnModel: TableColumnModel = {
        heading: 'Common.lhp.manageList.dialog.column.status',
        field: 'status',
        width: 100
    };

    protected typeCustomColumnModel: TableColumnModel = {
        field: 'type',
        width: 150,
        sortable: true,
        sortType: 'text'
    };

    protected alertTypeCustomColumnModel: TableColumnModel = {
        field: 'alertType',
        width: 150,
        sortable: true,
        sortType: 'text'
    };

    protected briefingDeliveryMethodCustomColumnModel: TableColumnModel = {
        field: 'deliveryMethod',
        width: 100,
        sortable: true,
        sortType: 'text'
    };

    protected countCustomColumnModel: TableColumnModel = {
        field: 'count',
        width: 100,
        sortable: true,
        sortType: 'numeric'
    };

    // For future implementation
    protected tagsCustomColumnModel: TableColumnModel = {
        type: TableColumnInputEnum.custom,
        heading: 'Common.lhp.manageList.dialog.column.tags',
        field: 'tags',
        width: 100,
        customTemplate: null
    };

    protected readonly statusFilter = new FilterTypeCheckboxModel<string>(
        'status',
        'Status',
        [],
        true
    );

    protected readonly commonFilters: FilterTypeModel[] = [
        this.nameFilter,
        this.createdByFilter,
        this.createdDateFilter,
        this.modifiedByFilter,
        this.modifiedDateFilter,
        this.visibilityFilter
    ];

    protected titleCasePipe = new TitleCasePipe();

    constructor(
        protected usersService: UsersService,
        public sidebarService: LeftSidebarService,
        protected displayGroupsService: DisplayGroupsService,
        protected localizationService: LocalizationService
    ) {
        super(usersService);
        this.sidebarService.getVisibilityFilterOptions(this.visibilityFilter);
        this.setFilters([
            this.nameFilter,
            this.createdByFilter,
            this.createdDateFilter,
            this.modifiedByFilter,
            this.modifiedDateFilter,
            this.visibilityFilter
        ]);
        this.subscriptions.push(
            this.sidebarService.getFilters().subscribe(lhpFilters => {
                this.lhpFilters = lhpFilters;
            }),
            this.displayGroupsService.getGroups().subscribe((groups) => {
                this.groupedListItems = groups.map(group => new DisplayGroupModel(group));
                this.groupItems();
            })
        );
    }

    abstract handleBulkItemAction(items: AuditableModel[], groups: DisplayGroupModel[], action: ActionsEnum): void;

    public getListItems(): Observable<AuditableModel[]> {
        return this.listItemsProvider$;
    }

    public getGroupedListItems(): Observable<DisplayGroupModel[]> {
        return this.groupedListItemsProvider$;
    }

    public getQuickFilterPlaceholder(): Observable<string> {
        return this.sidebarService.getQuickFilterPlaceholder();
    }

    public getTableColumns(): Observable<TableColumnModel[]> {
        return this.columnsProvider$;
    }

    public getLoading(): Observable<boolean> {
        return this.loading$;
    }

    public getResetSelection(): Observable<boolean> {
        return this.resetSelectionProvider$;
    }

    public handleItemAction(item: GroupedAuditableModel, action: ActionsEnum): void {
        const button: LeftSidebarListItemButton = {
            key: action,
            icon: null
        };
        const sidebarItem: LeftSidebarListItem = {
            label: item.name,
            value: item.id,
            status: item['status'],
            buttons: null,
            groupId: item.groupId
        };
        if (action === ActionsEnum.move) {
            this.sidebarService.moveListItem(sidebarItem);
        } else {
            this.sidebarService.listItemAction(sidebarItem, button, false);
        }
    }

    public setCustomData(rowData: ItemRow[]): ItemRow[] {
        return rowData;
    }

    protected setStatusData(rowData: ItemRow[]) {
        rowData.forEach(row => {
            const item = this.allListItems.find(x => x.id === row.original.id) as StatusLhpItem;
            row.status = this.titleCasePipe.transform(this.localizationService.transform(item.status));
        });
    }

    protected setCustomColumnHeadings() {
        this.countCustomColumnModel.heading = `Common.lhp.manageList.dialog.column.count.${this.currentMode}`;
        this.typeCustomColumnModel.heading = `Common.lhp.manageList.dialog.column.type.${this.currentMode}`;
        this.alertTypeCustomColumnModel.heading = 'Alerts.alertType';
        this.briefingDeliveryMethodCustomColumnModel.heading = 'Monitor.leftSidebar.briefings.type';
    }

    protected setUpFilters() {
        this.filters = this.filters.filter(filter => this.commonFilters.includes(filter));

        if (this.currentConfig?.customFilters) {
            this.currentConfig.customFilters.forEach(filter => {
                this.filters.push(filter);
            });

            this.currentConfig.setUpCustomFilters();
        }

        this.getAuditableFilterOptions(this.allListItems as AuditableModel[]);
        this.setFilters(this.filters);
        this.applyFilters();
    }


    /**
     * Applies the current set of filters to the list items
     */
    public applyFilters(): void {
        let filteredItems = this.applyCommonFilters();

        if (this.currentConfig?.applyCustomFilters) {
            filteredItems = this.currentConfig.applyCustomFilters(filteredItems);
        }

        this.listItems = filteredItems;
        this.listItemsProvider$.next(this.listItems);
        this.groupItems();
        this.loading$.next(false);
    }

    /**
     * Applies filters common to
     */
    protected applyCommonFilters(): GroupedAuditableModel[] {
        super.applyFilters();

        let filteredItems: GroupedAuditableModel[] = this.allListItems.slice();

        if (!filteredItems) {
            this.listItems = null;
            this.listItemsProvider$.next(this.listItems);

            return;
        }

        filteredItems = this.applyNameFilters(filteredItems);
        filteredItems = this.applyAuditableFilters(filteredItems);
        filteredItems = this.applyVisibilityFilter(filteredItems);

        if (this.filters.includes(this.statusFilter)) {
            filteredItems = this.applyStatusFilter(filteredItems);
        }

        return filteredItems;
    }

    protected applyStatusFilter(items: GroupedAuditableModel[]): GroupedAuditableModel[] {
        const value: string[] = this.statusFilter.getCurrentValue();

        if (!value || value.length < 1) {
            return items;
        }

        return items.filter(item => {
            const status = item['status'];
            return value.includes(status);
        });
    }

    protected applyVisibilityFilter(items: GroupedAuditableModel[]): GroupedAuditableModel[] {
        const value = this.visibilityFilter.getCurrentValue();
        switch(value) {
            case ListEntitiesVisibilityEnum.hidden:
                return items.filter(item => {
                    return item.hidden;
                });
            case ListEntitiesVisibilityEnum.visible:
                return items.filter(item => {
                    return !item.hidden;
                });
            case ListEntitiesVisibilityEnum.all:
            default:
                return items;
        }
    }

    public setVisibilityFilter(visibility: ListEntitiesVisibilityEnum = ListEntitiesVisibilityEnum.all) {
        this.visibilityFilter.setCurrentValue(visibility);
        this.applyFilters();
    }

    public resetFilters() {
        this.resetCommonFilters();
        if (this.currentConfig?.resetCustomFilters) {
            this.currentConfig.resetCustomFilters();
        }
    }

    public resetCommonFilters() {
        this.nameFilter.setValue([]);
        this.createdByFilter.setValue([]);
        this.createdDateFilter.setValue(DateQueryModel.getAllQuery());
        this.modifiedByFilter.setValue([]);
        this.modifiedDateFilter.setValue(DateQueryModel.getAllQuery());
        this.visibilityFilter.setValue(null);
        this.statusFilter.setValue([]);
    }

    public setLhpFilters() {
        this.resetFilters();
        this.filters.forEach(mgmtFilter => {
            const lhpFilterValue = this.lhpFilters.find(x => x.id === mgmtFilter.id).getValue();
            if (Array.isArray(lhpFilterValue) && lhpFilterValue.length) {
                mgmtFilter.setValue(lhpFilterValue);
            } else if (lhpFilterValue) {
                mgmtFilter.setValue(lhpFilterValue);
            }
        });
        this.applyFilters();
    }

    public createGroup() {
        this.sidebarService.createNewGroup();
    }

    public groupItems() {
        this.groupedListItems.forEach(group => {
            group.listItems = this.listItems.filter(item => item.groupId === group.id);
        });
        let uncategorized = this.groupedListItems.find(group => group.type === DisplayGroupTypeEnum.uncategorized);
        if (!uncategorized) {
            uncategorized = new DisplayGroupModel();
            uncategorized.name = this.localizationService.transform('Common.lhp.groups.uncategorized');
            uncategorized.type = DisplayGroupTypeEnum.uncategorized;
            this.groupedListItems.push(uncategorized);
        }
        uncategorized.listItems = this.listItems.filter(item => !item.groupId || !this.groupedListItems.find(group => group.id === item.groupId));

        let favorites = this.groupedListItems.find(group => group.type === DisplayGroupTypeEnum.favorites);
        if (!favorites) {
            favorites = new DisplayGroupModel();
            favorites.name = this.localizationService.transform('Common.lhp.groups.favorites');
            favorites.type = DisplayGroupTypeEnum.favorites;
            this.groupedListItems.unshift(favorites);
        }
        favorites.listItems = this.listItems.filter(item => item.isFavorite);
        this.groupedListItemsProvider$.next(this.groupedListItems);
    }

    public groupAction(group: DisplayGroupModel, action: ActionsEnum) {
        switch (action) {
            case ActionsEnum.rename:
                this.displayGroupsService.updateGroup(group).subscribe();
                break;
            case ActionsEnum.delete:
                const listItemIds = group.listItems.map(item => item.id);
                this.displayGroupsService.confirmDeleteGroup(group).subscribe((confirmed) => {
                    if (confirmed && listItemIds) {
                        this.sidebarService.handleGroupDelete(listItemIds);
                    }
                });
                break;
        }
    }

}
